import { useEffect } from 'react';
import { GOT_STREAK_FRAME, GOT_STREAK_STATUS } from 'constants/eventConstants';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';
import { trackPendoEvent } from 'services/PendoService';
import { getStreakStatus } from 'services/streaksService';
import { decompressData } from 'utils/functions';

import { openStreakPanel } from 'components/Streaks/Banner/LobbyStreakBanner';
import { msUntilCentralMidnight } from 'components/Streaks/model/centralTime';
import { StreakSnapshot } from 'components/Streaks/model/types';

import { resetSeatFrameLoader } from './seatFrameLoader';
import { STREAK_PUSH_OPENED, takeStreakPushOpened } from './streakPushOpened';
import { streakStore } from './streakStore';

/** Coming back to the app re-reads at most this often. */
const FOREGROUND_REFRESH_MS = 60_000;
/** After midnight CT, wait for the boundary job (00:05) plus a spread. */
const MIDNIGHT_BASE_MS = 5 * 60_000;
const MIDNIGHT_SPREAD_MS = 5 * 60_000;

/** Accepts `{ snapshot }`, `{ data: { snapshot } }` or the bare snapshot. */
export function normalizeStreakPayload(data: unknown): StreakSnapshot | null {
  const parsed = decompressData(data) as any;
  if (!parsed || typeof parsed !== 'object') return null;
  const raw = parsed.data ?? parsed;
  const snap = raw?.snapshot ?? raw;
  if (
    !snap ||
    typeof snap !== 'object' ||
    typeof snap.version !== 'number' ||
    !Array.isArray(snap.milestones)
  ) {
    return null;
  }
  return snap as StreakSnapshot;
}

/** `gotStreakFrame`: `{ playerGUID, frame, version }`, bare or under `data`. */
export function normalizeSeatFramePayload(
  data: unknown
): { playerGUID: string; frame: unknown; version: number } | null {
  const parsed = decompressData(data) as any;
  const raw = parsed?.data ?? parsed;
  if (!raw || typeof raw.playerGUID !== 'string' || typeof raw.version !== 'number') return null;
  return { playerGUID: raw.playerGUID, frame: raw.frame ?? null, version: raw.version };
}

/**
 * Keeps the streak snapshot fresh (EN-11151 "How clients get the data"):
 * one REST read on sign-in, then whatever game-ws pushes; a re-read when the
 * app comes back to the foreground, and once just after the midnight boundary
 * job. No polling loop: when nothing changes, nothing runs.
 */
export default function useStreakSync(socket: any) {
  const enabled = useFeatureFlag('streaks');
  const guest = useGuest();
  const playerGUID = useAppSelector((s) => s.user.playerGUID);
  const active = enabled && !guest && !!playerGUID;

  // A different (or no) player: drop the previous player's snapshot.
  useEffect(() => {
    streakStore.reset();
    resetSeatFrameLoader();
  }, [playerGUID, active]);

  // A tapped streak push (EN-11238): freeze covered opens the panel with the
  // streak card expanded; last call just opens the app. Handled once signed in,
  // so a tap that cold-starts the app waits for the UI.
  useEffect(() => {
    if (!active) return undefined;
    const handle = () => {
      const kind = takeStreakPushOpened();
      if (!kind) return;
      trackPendoEvent({
        playerGUID,
        eventName: 'streak_notification_opened',
        properties: { kind }
      });
      if (kind === 'freeze') openStreakPanel();
    };
    handle();
    window.addEventListener(STREAK_PUSH_OPENED, handle);
    return () => window.removeEventListener(STREAK_PUSH_OPENED, handle);
  }, [active, playerGUID]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let inFlight: AbortController | null = null;
    let lastRead = 0;
    let midnightTimer: ReturnType<typeof setTimeout> | null = null;

    const read = () => {
      inFlight?.abort();
      const controller = new AbortController();
      inFlight = controller;
      lastRead = Date.now();
      getStreakStatus(controller.signal)
        .then((snap) => {
          if (!cancelled) streakStore.accept(snap);
        })
        .catch(() => {
          // The surfaces hide until a snapshot exists; the next foreground or
          // push retries. Nothing to show the player for a failed read.
          if (!cancelled) streakStore.markLoaded();
        });
    };

    const scheduleMidnight = () => {
      const wait =
        msUntilCentralMidnight(Date.now()) + MIDNIGHT_BASE_MS + Math.random() * MIDNIGHT_SPREAD_MS;
      midnightTimer = setTimeout(() => {
        read();
        scheduleMidnight();
      }, wait);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRead > FOREGROUND_REFRESH_MS)
        read();
    };

    read();
    scheduleMidnight();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      inFlight?.abort();
      if (midnightTimer) clearTimeout(midnightTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, playerGUID]);

  useEffect(() => {
    if (!socket || !active) return undefined;
    const onStatus = (data: unknown) => {
      const snap = normalizeStreakPayload(data);
      if (snap) streakStore.accept(snap);
    };
    const onFrame = (data: unknown) => {
      const update = normalizeSeatFramePayload(data);
      if (update) streakStore.acceptSeatFrames({ [update.playerGUID]: update });
    };
    socket.on(GOT_STREAK_STATUS, onStatus);
    socket.on(GOT_STREAK_FRAME, onFrame);
    return () => {
      // Always pass the handler: off(event) alone drops every listener.
      socket.off(GOT_STREAK_STATUS, onStatus);
      socket.off(GOT_STREAK_FRAME, onFrame);
    };
  }, [socket, active]);
}
